import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { importApi, ImportRequest } from '../api/import';
import { exportApi } from '../api/export';
import PremiumLayout from '../components/PremiumLayout';
import { 
  ArrowUpTrayIcon,
  DocumentArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

const Import: React.FC = () => {
  const [entityType, setEntityType] = useState<'lead' | 'account' | 'contact' | 'activity' | 'task'>('lead');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);
  const queryClient = useQueryClient();

  const importMutation = useMutation({
    mutationFn: (request: ImportRequest) => importApi.import(request),
    onSuccess: (data) => {
      // Invalidate and refetch the relevant queries based on entity type
      if (data.imported > 0) {
        queryClient.invalidateQueries({ queryKey: [entityType + 's'] }); // leads, accounts, contacts, etc.
        if (entityType === 'lead') {
          queryClient.invalidateQueries({ queryKey: ['leads'] });
        } else if (entityType === 'account') {
          queryClient.invalidateQueries({ queryKey: ['accounts'] });
        } else if (entityType === 'contact') {
          queryClient.invalidateQueries({ queryKey: ['contacts'] });
        } else if (entityType === 'activity') {
          queryClient.invalidateQueries({ queryKey: ['activities'] });
        } else if (entityType === 'task') {
          queryClient.invalidateQueries({ queryKey: ['tasks'] });
        }
      }
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleDownloadSample = async () => {
    try {
      await exportApi.downloadSample(entityType);
    } catch (error) {
      alert('Failed to download sample file. Please try again.');
      console.error('Error downloading sample:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      alert('Please select a file to import');
      return;
    }

    const request: ImportRequest = {
      entity_type: entityType,
      file: selectedFile,
      options: {
        skipDuplicates,
        updateExisting,
      },
    };

    importMutation.mutate(request);
  };

  return (
    <PremiumLayout
      title="Import Data"
      subtitle="Import data from CSV or Excel files"
    >
      <div className="max-w-2xl mx-auto">
        <div className="premium-card p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Entity Type */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Entity Type
                </label>
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  className="text-sm text-indigo-600 hover:text-indigo-700 font-medium flex items-center"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                  Download Sample
                </button>
              </div>
              <select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value as any)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="lead">Leads</option>
                <option value="account">Accounts</option>
                <option value="contact">Contacts</option>
                <option value="activity">Activities</option>
                <option value="task">Tasks</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Download a sample Excel template with all required columns and example data
              </p>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select File
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-indigo-400 transition-colors">
                <div className="space-y-1 text-center">
                  <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none">
                      <span>Upload a file</span>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileChange}
                        className="sr-only"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">CSV, XLSX up to 10MB</p>
                  {selectedFile && (
                    <p className="text-sm text-indigo-600 mt-2">{selectedFile.name}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={skipDuplicates}
                  onChange={(e) => setSkipDuplicates(e.target.checked)}
                  className="mr-2"
                />
                Skip duplicate records
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  className="mr-2"
                />
                Update existing records
              </label>
            </div>

            {/* Import Button */}
            <button
              type="submit"
              disabled={!selectedFile || importMutation.isPending}
              className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {importMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                  Importing...
                </>
              ) : (
                <>
                  <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                  Import Data
                </>
              )}
            </button>

            {/* Results */}
            {importMutation.data && (
              <div className={`p-4 rounded-lg ${
                importMutation.data.imported > 0 ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {importMutation.data.imported > 0 ? (
                  <div>
                    <div className="flex items-center mb-2">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      <p className="font-semibold">Import Completed!</p>
                    </div>
                    <div className="ml-7 space-y-1">
                      <p>✅ Successfully imported: <strong>{importMutation.data.imported}</strong> records</p>
                      {importMutation.data.failed > 0 && (
                        <p>❌ Failed: <strong>{importMutation.data.failed}</strong> records</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <XCircleIcon className="h-5 w-5 mr-2" />
                    <div>
                      <p className="font-semibold">Import Failed</p>
                      <p>No records were imported</p>
                    </div>
                  </div>
                )}
                
                {/* Show detailed errors if any */}
                {importMutation.data.errors && importMutation.data.errors.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    <p className="font-semibold mb-2">Error Details:</p>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {importMutation.data.errors.map((error: any, idx: number) => (
                        <div key={idx} className="bg-white p-2 rounded text-sm">
                          <p className="font-medium">Row {error.row}: {error.error}</p>
                          {error.data && (
                            <p className="text-xs text-gray-600 mt-1">
                              Data: {JSON.stringify(error.data).substring(0, 100)}...
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Error from mutation */}
            {importMutation.error && (
              <div className="p-4 rounded-lg bg-red-50 text-red-800">
                <div className="flex items-center">
                  <XCircleIcon className="h-5 w-5 mr-2" />
                  <div>
                    <p className="font-semibold">Import Error</p>
                    <p className="text-sm mt-1">
                      {(importMutation.error as any)?.response?.data?.error || 
                       (importMutation.error as any)?.message || 
                       'An error occurred during import'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </PremiumLayout>
  );
};

export default Import;
